import { useCallback, useEffect, useRef, useState } from 'react';
import {
    parsePdfFileNameFromContentDisposition,
    revokeObjectUrlIfOwned,
} from '../utils/pdfPreviewUtils';
import { resolveSignedMediaSrc } from '../utils/imageProxy';

const DEFAULT_FILE_NAME = 'document.pdf';
const DEFAULT_TITLE = 'Document PDF';

/**
 * Manages a single PDF preview session (remote URL or in-memory blob).
 * Cleans up object URLs on close, download, print, and unmount — no accumulation.
 */
export function usePdfPreview() {
    const sessionRef = useRef({
        blob: null,
        objectUrl: null,
        ownsObjectUrl: false,
        fileName: DEFAULT_FILE_NAME,
        title: DEFAULT_TITLE,
    });

    const [open, setOpen] = useState(false);
    const [fileUrl, setFileUrl] = useState(null);
    const [fileName, setFileName] = useState(DEFAULT_FILE_NAME);
    const [title, setTitle] = useState(DEFAULT_TITLE);
    const [loading, setLoading] = useState(false);

    const cleanupSession = useCallback(() => {
        const session = sessionRef.current;
        revokeObjectUrlIfOwned(session.objectUrl, session.ownsObjectUrl);
        sessionRef.current = {
            blob: null,
            objectUrl: null,
            ownsObjectUrl: false,
            fileName: DEFAULT_FILE_NAME,
            title: DEFAULT_TITLE,
        };
        setFileUrl(null);
        setFileName(DEFAULT_FILE_NAME);
        setTitle(DEFAULT_TITLE);
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        setLoading(false);
        cleanupSession();
    }, [cleanupSession]);

    const openFromUrl = useCallback(async (url, options = {}) => {
        cleanupSession();
        const nextFileName = options.fileName || DEFAULT_FILE_NAME;
        const nextTitle = options.title || nextFileName || DEFAULT_TITLE;
        const sessionId = Symbol('pdf-preview');

        sessionRef.current = {
            blob: null,
            objectUrl: null,
            ownsObjectUrl: false,
            fileName: nextFileName,
            title: nextTitle,
            sessionId,
        };

        setFileName(nextFileName);
        setTitle(nextTitle);
        setFileUrl(null);
        setOpen(true);
        setLoading(true);

        const resolved = await resolveSignedMediaSrc(url);
        if (sessionRef.current.sessionId !== sessionId) {
            return;
        }

        sessionRef.current.objectUrl = resolved;
        setFileUrl(resolved);
        setLoading(false);
    }, [cleanupSession]);

    const openFromBlob = useCallback((blob, options = {}) => {
        cleanupSession();
        const nextFileName = options.fileName || DEFAULT_FILE_NAME;
        const nextTitle = options.title || nextFileName || DEFAULT_TITLE;
        const objectUrl = URL.createObjectURL(blob);

        sessionRef.current = {
            blob,
            objectUrl,
            ownsObjectUrl: true,
            fileName: nextFileName,
            title: nextTitle,
        };

        setFileName(nextFileName);
        setTitle(nextTitle);
        setFileUrl(objectUrl);
        setOpen(true);
        setLoading(false);
    }, [cleanupSession]);

    const openLoadingPreview = useCallback((options = {}) => {
        cleanupSession();
        const nextFileName = options.fileName || DEFAULT_FILE_NAME;
        const nextTitle = options.title || nextFileName || DEFAULT_TITLE;

        sessionRef.current = {
            blob: null,
            objectUrl: null,
            ownsObjectUrl: false,
            fileName: nextFileName,
            title: nextTitle,
        };

        setFileName(nextFileName);
        setTitle(nextTitle);
        setFileUrl(null);
        setLoading(true);
        setOpen(true);
    }, [cleanupSession]);

    const openFromAxiosBlobResponse = useCallback((response, options = {}) => {
        const session = sessionRef.current;
        revokeObjectUrlIfOwned(session.objectUrl, session.ownsObjectUrl);

        const fallbackFileName = options.fallbackFileName || session.fileName || DEFAULT_FILE_NAME;
        const resolvedFileName = parsePdfFileNameFromContentDisposition(
            response.headers?.['content-disposition'],
            fallbackFileName,
        );
        const nextTitle = options.title || session.title || resolvedFileName;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(blob);

        sessionRef.current = {
            blob,
            objectUrl,
            ownsObjectUrl: true,
            fileName: resolvedFileName,
            title: nextTitle,
        };

        setFileName(resolvedFileName);
        setTitle(nextTitle);
        setFileUrl(objectUrl);
        setLoading(false);
        setOpen(true);
    }, []);

    const getBlob = useCallback(() => sessionRef.current.blob, []);

    useEffect(() => () => cleanupSession(), [cleanupSession]);

    return {
        open,
        loading,
        setLoading,
        close,
        openLoadingPreview,
        openFromUrl,
        openFromBlob,
        openFromAxiosBlobResponse,
        getBlob,
        dialogProps: {
            open,
            onClose: close,
            fileUrl,
            fileName,
            title,
            loading,
            getBlob,
        },
    };
}
